// Library Methods
import { useCallback, useEffect, useRef, useState } from "react";

// Components
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import DownloadButtonContent from "./DownloadButtonContent/DownloadButtonContent";

// Constants
import {
  ReportFileType,
  reportFileTypes,
  ReportStatus,
} from "src/constants/enums";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";

// Types
import { FileInfoType } from "src/types/sharedTypes";

type DownloadButtonsType = {
  isLoading: boolean;
  fileInfo: { [key in ReportFileType]: FileInfoType };
  reportInfoError: boolean;
  showDICOMButton: boolean;
  showPNGButton: boolean;
  showDownloadButtons: boolean;
  downloadFile: (fileType: ReportFileType) => void;
};

const DownloadButtons = ({
  isLoading,
  fileInfo,
  reportInfoError,
  showDICOMButton,
  showPNGButton,
  showDownloadButtons,
  downloadFile,
}: DownloadButtonsType) => {
  const firstLoadingIndicator = useRef(true);
  const [firstLoadingDone, setFirstLoadingDone] = useState<boolean | null>(
    null
  );

  // ensures the user is under the first loading before they receive report info data.
  useEffect(() => {
    if (firstLoadingIndicator.current === false) return;

    let reportInfoDataIsReady = false;
    Object.keys(fileInfo).forEach((type) => {
      const formattedType = type as ReportFileType;
      if (fileInfo?.[formattedType]?.reportInfoData)
        reportInfoDataIsReady = true;
    });

    if (reportInfoDataIsReady) {
      firstLoadingIndicator.current = false;
      setFirstLoadingDone(true);
    }
  }, [fileInfo]);

  const showDownloadButton = useCallback(
    (fileType: ReportFileType) => {
      switch (fileType) {
        case "DICOM":
          return showDICOMButton;
        case "PNG":
          return showPNGButton;
        default:
          return true;
      }
    },
    [showDICOMButton, showPNGButton]
  );

  const isDisabledButton = useCallback(
    (fileType: ReportFileType) =>
      reportInfoError ||
      fileInfo?.[fileType]?.reportInfoData?.status !== ReportStatus.Ready,
    [fileInfo, reportInfoError]
  );

  if (isLoading) {
    return (
      <>
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            sx={{
              height: 40,
              width: { xs: "100%", sm: "200px" },
              borderRadius: "4px",
            }}
          />
        ))}
      </>
    );
  }

  if (!isLoading && !showDownloadButtons) {
    return null;
  }

  return (
    <>
      {reportFileTypes.map((fileType: ReportFileType) => {
        const isHidden = !showDownloadButton(fileType);
        const isDisabled = isDisabledButton(fileType);

        return (
          <Button
            key={fileType}
            hidden={isHidden}
            disabled={isDisabled}
            sx={{
              minWidth: "200px",
              width: { xs: "100%", sm: "fit-content" },
              border: "1px solid",
              borderColor: isDisabled ? "#00000043" : "#000",
              color: isDisabled ? "#00000043" : "#000",
              height: `${UI_SIZES.inputHeight}px`,
              textTransform: "capitalize",

              "&:hover": {
                backgroundColor: "#EBEBEB",
              },
            }}
            onClick={() => downloadFile(fileType)}
          >
            <DownloadButtonContent
              fileData={fileInfo[fileType]}
              fileType={fileType}
              reportInfoError={reportInfoError}
              firstLoadingDone={firstLoadingDone}
            />
          </Button>
        );
      })}
    </>
  );
};

export default DownloadButtons;
